import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    InputBase,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty, isObject, isString } from 'lodash';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    FormOption,
    IRegistrationForm,
    IRegistrationFormField,
    TSearchAddCategory,
} from '../../types/global/forms.types';
import ActionRenderer, { IActionRenderer } from '../ActiobButtonComponents/ActionRenderer';
import CustomButton from '../Button/CustomButton'; // Importing custom button component
import { ChipGroupWithMultiSelection } from '../Chip/ChipGroupWithMultiSelection';
import { CurrencyTextInput } from '../CurrencyTextInput/CurrencyTextInput';
import CustomizedAutocomplete from '../CustomizedMultipleInput/CustomizedAutocomplete';
import { CustomizedMultipleInput } from '../CustomizedMultipleInput/CustomizedMultipleInput';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker'; // Importing custom date picker component
import PictureUploader from '../PictureUploader/PictureUploader';
import SearchSelectComponent from '../SearchAdd/SearchSelectComponent';
import UploadComponent from '../Upload/UploadComponent';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import _ from 'lodash';
import MediaAdderComponent from '../MediaAdderComponent/MediaAdderComponent';
import { extensionToMimeType } from '../Upload/data';
import { CustomHeaderWithStatus } from '../EsForm/CustomHeaderWithStatus';
import { isEmptyValue } from '../../utils/lodash';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { useDispatch, useSelector } from 'react-redux';
import { saveFormDraft, clearFormDraft } from '../../redux/appConfigurator/action';
import { createFormDraftSelector } from '../../selectors/selectors';
import { RichTextEditor } from '../Common/RichTextEditor';
import { AUDITION_RICH_TEXT_STORE_KEYS } from '../../utils/auditionRichTextCopy';
import {
    BRAND_MODAL_ACCENT,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    brandModalFieldGutterSx,
    brandModalOutlinedInputSx,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import {
    EDITORIAL,
    editorialCharCountSx,
    editorialDropdownBentoSx,
    editorialInputShellSx,
    editorialMenuPaperSx,
    editorialOutlinedInputSx,
    editorialRangeSectionSx,
    editorialScrollRootSx,
    editorialSelectSx,
    editorialSliderSx,
    editorialStickyFooterSx,
    editorialSubmitButtonSx,
    editorialSubmitTextStyle,
} from './editorialFormStyles';

const hideNativeNumberSpinnerSx = {
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
    },
    '& input[type=number]': {
        MozAppearance: 'textfield' as const,
    },
};

const brandRangeStepperShellSx = {
    display: 'flex',
    alignItems: 'stretch',
    borderRadius: '12px',
    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
    overflow: 'hidden',
    minHeight: 48,
};

const brandRangeStepBtnSx = {
    flexShrink: 0,
    width: 48,
    minWidth: 48,
    height: 'auto',
    alignSelf: 'stretch',
    borderRadius: 0,
    color: BRAND_MODAL_ACCENT,
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    '&:hover': {
        backgroundColor: 'rgba(232, 194, 125, 0.14)',
    },
    '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.22)',
    },
};

/** Brand `range` fields: min/max with optional number vs text input, ± steppers, clamp to bounds. */
const BrandRangePairFields: React.FC<{
    pair: [number, number];
    minBound: number;
    maxBound: number;
    bumpStep: number;
    fieldStyle: Record<string, unknown>;
    header: React.ReactNode;
    /** `number`: native number field (spinners hidden); ± buttons use `bumpStep`. `text`: free typing + numeric keyboard. */
    inputVariant: 'text' | 'number';
    onCommit: (lo: number, hi: number) => void;
}> = ({ pair, minBound, maxBound, bumpStep, fieldStyle: _unusedFieldStyle, header, inputVariant, onCommit }) => {
    const [minDraft, setMinDraft] = useState<string | null>(null);
    const [maxDraft, setMaxDraft] = useState<string | null>(null);

    const clamp = (n: number) => Math.max(minBound, Math.min(maxBound, n));

    const commitLoHi = (lo: number, hi: number) => {
        const x = clamp(lo);
        const y = clamp(hi);
        const loOk = Math.min(x, y);
        const hiOk = Math.max(x, y);
        onCommit(loOk, hiOk);
    };

    const pairLo = pair[0];
    const pairHi = pair[1];
    useEffect(() => {
        setMinDraft(null);
        setMaxDraft(null);
    }, [pairLo, pairHi]);

    const minShown = minDraft !== null ? minDraft : String(pair[0]);
    const maxShown = maxDraft !== null ? maxDraft : String(pair[1]);

    const flushMin = () => {
        if (minDraft === null) return;
        const raw = minDraft.trim();
        if (raw === '') {
            commitLoHi(pair[0], pair[1]);
        } else {
            const n = Number(raw.replace(/,/g, ''));
            if (!Number.isFinite(n)) {
                commitLoHi(pair[0], pair[1]);
            } else {
                commitLoHi(n, pair[1]);
            }
        }
        setMinDraft(null);
    };

    const flushMax = () => {
        if (maxDraft === null) return;
        const raw = maxDraft.trim();
        if (raw === '') {
            commitLoHi(pair[0], pair[1]);
        } else {
            const n = Number(raw.replace(/,/g, ''));
            if (!Number.isFinite(n)) {
                commitLoHi(pair[0], pair[1]);
            } else {
                commitLoHi(pair[0], n);
            }
        }
        setMaxDraft(null);
    };

    const labelSx = {
        fontSize: 12,
        color: 'rgba(255,255,255,0.55)',
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase' as const,
        mb: 0.5,
    };

    const centerInputSx = {
        flex: 1,
        minWidth: 0,
        ...hideNativeNumberSpinnerSx,
        '& input': {
            textAlign: 'center' as const,
            color: 'rgba(255,255,255,0.95)',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            fontSize: 16,
            py: 1.25,
            MozAppearance: 'textfield' as const,
        },
    };

    const renderStepperColumn = (which: 'min' | 'max') => {
        const isMin = which === 'min';
        const shown = isMin ? minShown : maxShown;
        const setDraft = isMin ? setMinDraft : setMaxDraft;
        const flush = isMin ? flushMin : flushMax;
        const onFocusDraft = () => (isMin ? setMinDraft(String(pair[0])) : setMaxDraft(String(pair[1])));
        const dec = () => {
            if (isMin) {
                commitLoHi(pair[0] - bumpStep, pair[1]);
            } else {
                commitLoHi(pair[0], pair[1] - bumpStep);
            }
            setMinDraft(null);
            setMaxDraft(null);
        };
        const inc = () => {
            if (isMin) {
                commitLoHi(pair[0] + bumpStep, pair[1]);
            } else {
                commitLoHi(pair[0], pair[1] + bumpStep);
            }
            setMinDraft(null);
            setMaxDraft(null);
        };
        const decDisabled = isMin ? pair[0] <= minBound : pair[1] <= minBound;
        const incDisabled = isMin ? pair[0] >= maxBound : pair[1] >= maxBound;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography component="label" sx={labelSx}>
                    {isMin ? 'Minimum' : 'Maximum'}
                </Typography>
                <Box sx={brandRangeStepperShellSx}>
                    <IconButton
                        type="button"
                        aria-label={isMin ? 'Decrease minimum' : 'Decrease maximum'}
                        disabled={decDisabled}
                        onMouseDown={e => e.preventDefault()}
                        onClick={dec}
                        sx={brandRangeStepBtnSx}>
                        −
                    </IconButton>
                    <InputBase
                        fullWidth
                        type={inputVariant === 'number' ? 'number' : 'text'}
                        inputMode={inputVariant === 'number' ? undefined : 'numeric'}
                        value={shown}
                        onFocus={onFocusDraft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={flush}
                        inputProps={
                            inputVariant === 'number' ? { min: minBound, max: maxBound, step: bumpStep } : undefined
                        }
                        sx={centerInputSx}
                    />
                    <IconButton
                        type="button"
                        aria-label={isMin ? 'Increase minimum' : 'Increase maximum'}
                        disabled={incDisabled}
                        onMouseDown={e => e.preventDefault()}
                        onClick={inc}
                        sx={brandRangeStepBtnSx}>
                        +
                    </IconButton>
                </Box>
            </Box>
        );
    };

    return (
        <>
            {header}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    {renderStepperColumn('min')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    {renderStepperColumn('max')}
                </Grid>
            </Grid>
        </>
    );
};

const isDev = false;

export interface IFormGeneratorWithoutModalProps
    extends Pick<IActionRenderer, 'onDelete' | 'onDuplicate' | 'onEdit' | 'inlineMode'> {
    onClose: () => void;
    header?: string; // Optional header for the form
    onSubmit?: (payload: Record<any, any>) => void;
    onAddClicked?: (category?: TSearchAddCategory, formId?: string, currentFormState?: any, index?: number) => void;
    /** formId indicates the form that will be opened, the storeKey is the key under which will be stored.
     * if there is a parent key then the storekey will be store inside the parentkey
     */
    onCreateNewClicked?: (formId?: string, storeKey?: string, currentFormState?: any, index?: number) => void;
    initialValue?: any;
    isEdit?: boolean;
    initialEditValue?: any;
    form?: IRegistrationForm;
    forceUpdate?: boolean;
    cancelForceUpdate?: () => void;
    isTopScreen?: boolean;
    onSave?: () => void;
    onChange?: (storeKey: string, subKey?: string, value?: any) => void;
    devData?: any;
    loading?: boolean;
    /** Allows us to manually trigger the close for this modal instead of auto close on submit
     * Helps when we want the api call completed before closing
     */
    manualClose?: boolean;
    /** When passed, these fields can be ignore and will not be rendered */
    exemptedFields?: string[];
    /** Form type identifier for draft management */
    formType?: string;
    /** Whether to enable draft saving */
    enableDraftSaving?: boolean;
    /** Callback to get unsaved changes status */
    onUnsavedChangesChange?: (hasChanges: boolean) => void;
    /** When using as chidl form. use this */
    initialFormData?: Record<string, any>;
    handleSubmitInternally?: boolean;
    /** Brand dashboard: borderless fields + subtle gold focus (audition / UGC modals). */
    uiVariant?: 'default' | 'brand';
}

export interface IFormGeneratorWithoutModalRef {
    saveDraftAndClose(formData?: Record<string, any>): void;
    closeWithoutSavingDraft(): void;
    getCurrentFormData(): Record<string, any>;
    handleSubmit(): void;
    disableButton(): boolean;
}

const FormGeneratorWithoutModal = React.forwardRef<IFormGeneratorWithoutModalRef, IFormGeneratorWithoutModalProps>(
    (
        {
            onClose,
            form,
            onSubmit,
            onAddClicked,
            onCreateNewClicked,
            initialValue = {},
            isEdit = false,
            initialEditValue = {},
            forceUpdate,
            cancelForceUpdate,
            isTopScreen = true,
            onSave,
            onChange,
            devData,
            loading,
            manualClose,
            exemptedFields,
            formType,
            enableDraftSaving = true,
            onUnsavedChangesChange,
            initialFormData,
            handleSubmitInternally = true,
            uiVariant = 'default',
            ...props
        },
        ref,
    ) => {
        const dispatch = useDispatch();
        const [formData, setFormData] = useState<{ [key: string]: any }>(!isEdit ? initialValue : initialEditValue);
        const formFields = [...(form?.form ?? [])].sort((a, b) => a.position - b.position);
        const { themeStyle } = useThemeContext();
        const isBrandForm = uiVariant === 'brand';
        /** The value below allows us set the initial value and it helps avoid re rendring */
        const initialValueSet = useRef<any>(false);
        const [checked, setChecked] = useState(false);

        const handleCloseWithoutSavingDraft = useCallback(() => {
            onClose();
        }, [onClose]);

        const handleSaveDraftAndClose = useCallback(
            (formDataToSave?: Record<string, any>) => {
                const dataToSave = formDataToSave || formData;
                if (enableDraftSaving && formType) {
                    console.log('💾 Saving draft for form:', formType, dataToSave);
                    dispatch(saveFormDraft({ formType, formData: dataToSave }));
                }
                onClose();
            },
            [enableDraftSaving, formType, formData, dispatch, onClose],
        );

        const handleSubmit = () => {
            if (isTopScreen) {
                onSubmit && onSubmit(formData);

                // Clear draft on successful save
                if (enableDraftSaving && formType) {
                    dispatch(clearFormDraft({ formType }));
                }

                if (!manualClose) onClose();
                setFormData({});
            } else {
                /** Simply Save the form item and control how it behaves */
                onSave && onSave();
            }
        };

        useImperativeHandle(ref, () => ({
            closeWithoutSavingDraft: handleCloseWithoutSavingDraft,
            saveDraftAndClose: handleSaveDraftAndClose,
            getCurrentFormData: () => formData,
            handleSubmit: handleSubmit,
            disableButton: () => !isFormComplete() || (isEdit && !hasChanged()),
        }));

        // Draft management
        const draftData = useSelector(createFormDraftSelector(formType || ''));
        const shouldLoadDraft = enableDraftSaving && formType && !isEdit && !initialValueSet.current;

        // Load draft data when component mounts
        useEffect(() => {
            if (shouldLoadDraft && draftData) {
                console.log('🔄 Loading draft data for form:', formType, draftData);
                setFormData(draftData);
                initialValueSet.current = true;
            }
        }, [shouldLoadDraft, draftData, formType]);

        // Check if there are unsaved changes (excluding draft data)
        const hasUnsavedChanges = useCallback(() => {
            if (isEdit) {
                const isDifferent = !_.isEqual(initialEditValue, formData);
                console.log('🔍 Edit mode - comparing:', { initialEditValue, formData, isDifferent });
                return isDifferent;
            }

            // For new forms, check if any field has a meaningful value
            const meaningfulFields = Object.keys(formData).filter(key => {
                const value = formData[key];
                // Check for meaningful values (not empty, null, undefined, or empty string)
                if (value === null || value === undefined || value === '') {
                    return false;
                }
                // Check for arrays
                if (Array.isArray(value)) {
                    return value.length > 0;
                }
                // Check for objects
                if (typeof value === 'object') {
                    return Object.keys(value).length > 0;
                }
                return true;
            });

            return meaningfulFields.length > 0;
        }, [initialEditValue, isEdit, formData]);

        // Notify parent about unsaved changes
        useEffect(() => {
            if (onUnsavedChangesChange) {
                const hasChanges = hasUnsavedChanges();
                onUnsavedChangesChange(hasChanges);
            }
        }, [onUnsavedChangesChange, hasUnsavedChanges, formType, formData, initialValue, isEdit]);

        const handleSetInitialData = useCallback(() => {
            /** The below seems to be the only way for this to work
             * when parent_key is not there we should make this update
             */
            if (!_.isEqual(formData, initialValue)) {
                setFormData(initialValue);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [initialValue]);

        useEffect(() => {
            // Only set the initial data if not already set
            // handleSetInitialData();
            if (forceUpdate) {
                handleSetInitialData();
            }
        }, [handleSetInitialData, forceUpdate]);

        const handleUpdateSpecificFields = useCallback(() => {
            const keys = Object.keys(initialValue);
            keys.forEach(key => {
                setFormData(prevState => ({
                    ...prevState,
                    [key]: initialValue[key],
                }));
            });
        }, [initialValue]);

        useEffect(() => {
            handleUpdateSpecificFields();
        }, [handleUpdateSpecificFields]);

        //TODO: This will fix duplicate and editing
        useEffect(() => {
            // Only set the initial data if not already set
            // handleSetInitialData();
            if (initialEditValue && isEdit) {
                setFormData(initialEditValue);
            }
        }, [initialEditValue, isEdit]);

        const { isLoaded } = useGoogleMapsLoader();

        const handleChange = (storeKey: string, value: string | number | Date | null | Record<any, any> | boolean) => {
            /** CHange in motion */
            setFormData(prevData => ({
                ...prevData,
                [storeKey]: value,
            }));
            onChange && onChange(storeKey, undefined, value);
        };

        const handleLocationSelect = (storeKey: string, address: string, isMultiple?: boolean) => {
            let description: any;
            let city: string;
            let country: string;
            let payload: any;

            geocodeByAddress(address)
                .then(results => {
                    description = results[0].formatted_address;
                    const addressComponents = results[0].address_components;
                    addressComponents.forEach(component => {
                        const types = component.types;
                        if (types.includes('locality')) {
                            city = component.long_name;
                        }
                        if (types.includes('country')) {
                            country = component.long_name;
                        }
                    });

                    return getLatLng(results[0]);
                })
                .then(latLng => {
                    setFormData(prevData => {
                        // Ensure prevData[storeKey] is treated as an array safely
                        const existingData = Array.isArray(prevData[storeKey]) ? prevData[storeKey] : [];

                        const newEntry = {
                            address: address,
                            type: 'Point',
                            coordinates: [latLng.lng, latLng.lat],
                            description: description,
                            short_name: `${city ? city : ''}, ${country}`,
                        };

                        // If isMultiple, add to the array; otherwise, replace with the new entry
                        payload = isMultiple ? [...existingData, newEntry] : newEntry;

                        return {
                            ...prevData,
                            [storeKey]: payload,
                        };
                    });
                    onChange && onChange(storeKey, undefined, payload);
                })
                .catch(error => console.error('Error', error));
        };

        function capitalizeFirst(str: string) {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        }

        const renderHeader = (field: IRegistrationFormField, ignoreBottomMargin?: boolean) => {
            return (
                <CustomHeaderWithStatus
                    field={field.store_key}
                    isRequired={field.required}
                    headerTitle={capitalizeFirst(field.form_header!)}
                    themeStyle={themeStyle}
                    state={formData}
                    editorial
                    style={{
                        marginBottom: ignoreBottomMargin ? 0 : '12px',
                    }}
                />
            );
        };

        const handleOnAddClickForSearchSuggestion = (searchCategory?: TSearchAddCategory, formId?: string) => {
            if (searchCategory && formId) {
                onAddClicked && onAddClicked(searchCategory, formId, formData);
            }
        };

        const renderField = (field: IRegistrationFormField) => {
            if (exemptedFields && exemptedFields?.includes(field.store_key)) {
                return;
            }
            const fieldStyle = {
                ...editorialOutlinedInputSx,
            };

            if (field.dependency) {
                const dependentValue = formData[field.dependency.depends_on];
                const condition = field.dependency.condition;

                let shouldRender = false;
                if (isObject(dependentValue) && 'value' in dependentValue) {
                    shouldRender =
                        condition?.equality === '!=='
                            ? dependentValue.value !== condition?.value
                            : dependentValue.value === condition?.value;
                } else {
                    shouldRender =
                        condition?.equality === '!=='
                            ? dependentValue !== condition?.value
                            : dependentValue === condition?.value;
                }

                if (isString(dependentValue) || dayjs(dependentValue).isValid()) {
                    shouldRender = !isEmpty(dependentValue);
                }

                if (!shouldRender) {
                    return null; // Skip rendering the field if dependency is not met
                }
            }

            switch (field.form_type) {
                case 'textInput': {
                    const useRichText =
                        AUDITION_RICH_TEXT_STORE_KEYS.has(field.store_key) || Boolean(field.textInputProps?.multiline);
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            {useRichText ? (
                                <RichTextEditor
                                    value={formData[field.store_key] || ''}
                                    key={field.form_name}
                                    shellVariant={isBrandForm ? 'brandDashboard' : 'default'}
                                    onChange={value => {
                                        handleChange(
                                            field.store_key,
                                            field.textInputProps?.type === 'number' ? Number(value) : value,
                                        );
                                    }}
                                />
                            ) : isBrandForm ? (
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    name={field.form_name}
                                    {...field.textInputProps}
                                    value={formData[field.store_key] || ''}
                                    onChange={e =>
                                        handleChange(
                                            field.store_key,
                                            field.textInputProps?.type === 'number'
                                                ? Number(e.target.value)
                                                : e.target.value,
                                        )
                                    }
                                    variant="outlined"
                                    sx={{
                                        ...fieldStyle,
                                        ...brandModalOutlinedInputSx,
                                        ...(field.textInputProps?.sx as object),
                                    }}
                                    key={field.form_name}
                                    InputProps={{
                                        ...(field.textInputProps?.InputProps || {}),
                                        sx: {
                                            fontSize: '14px',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: EDITORIAL.onSurface,
                                            ...((field.textInputProps?.InputProps as { sx?: object })?.sx || {}),
                                        },
                                    }}
                                    inputProps={{
                                        maxLength: field.textInputProps!?.maxchars,
                                        ...(field.textInputProps?.inputProps || {}),
                                    }}
                                />
                            ) : (
                                <Box sx={editorialInputShellSx}>
                                    <TextField
                                        fullWidth
                                        margin="dense"
                                        name={field.form_name}
                                        value={formData[field.store_key] || ''}
                                        onChange={e =>
                                            handleChange(
                                                field.store_key,
                                                field.textInputProps?.type === 'number'
                                                    ? Number(e.target.value)
                                                    : e.target.value,
                                            )
                                        }
                                        sx={fieldStyle}
                                        key={field.form_name}
                                        InputProps={{
                                            sx: {
                                                fontSize: '14px',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: EDITORIAL.onSurface,
                                            },
                                        }}
                                        inputProps={{
                                            maxLength: field.textInputProps!?.maxchars,
                                        }}
                                        {...field.textInputProps}
                                    />
                                </Box>
                            )}
                            {field.textInputProps!?.maxchars && (
                                <Typography variant="caption" sx={editorialCharCountSx}>
                                    {formData[field.store_key]?.length || 0}/{field.textInputProps!?.maxchars}
                                </Typography>
                            )}
                        </div>
                    );
                }
                case 'phone':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <PhoneInput
                                onChange={(e: any, data: CountryData) => handleChange(field.store_key, e)}
                                value={(formData[field.store_key] as string) || ''}
                                country="us"
                                specialLabel=""
                                inputStyle={{
                                    width: '100%',
                                    paddingLeft: isBrandForm ? 16 : undefined,
                                    paddingRight: isBrandForm ? 16 : 10,
                                    backgroundColor: EDITORIAL.surfaceContainer,
                                    border: `1px solid ${EDITORIAL.outlineVariant}`,
                                    borderRadius: 12,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: 14,
                                    color: EDITORIAL.onSurface,
                                    position: 'relative',
                                    backgroundSize: '20px',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right',
                                    backgroundOrigin: 'content-box',
                                    marginTop: '10px',
                                    boxShadow: isBrandForm ? 'none' : undefined,
                                }}
                                dropdownStyle={{
                                    backgroundColor: EDITORIAL.surfaceContainer,
                                    color: EDITORIAL.onSurface,
                                    width: '400px',
                                    border: isBrandForm ? `1px solid ${BRAND_OUTLINE_VARIANT}` : undefined,
                                }}
                                countryCodeEditable={false}
                            />
                        </div>
                    );
                case 'dropdown':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <Box sx={editorialDropdownBentoSx}>
                                <Select
                                    fullWidth
                                    margin="dense"
                                    name={field.form_name}
                                    value={formData[field.store_key] || ''}
                                    onChange={e => handleChange(field.store_key, e.target.value)}
                                    sx={{
                                        ...editorialSelectSx,
                                        textTransform: 'capitalize',
                                        mt: 0,
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: editorialMenuPaperSx,
                                        },
                                    }}
                                    key={field.form_name}
                                    displayEmpty>
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {(field.form_options as FormOption[]).map(option => (
                                        <MenuItem
                                            value={option.value}
                                            key={option.value}
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: EDITORIAL.onSurface,
                                            }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </div>
                    );
                case 'pillSelection':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <ChipGroupWithMultiSelection
                                clickable
                                editorial
                                options={field.form_options}
                                onTagClick={tag => handleChange(field.store_key, tag)}
                                initialSelected={(formData[field.store_key] as any) || []}
                                multiple={field.pillSelectionProps?.multiple}
                                {...field.pillSelectionProps}
                                variant={isBrandForm ? 'brand' : 'default'}
                            />
                        </div>
                    );
                case 'location':
                    return (
                        isLoaded && (
                            <div key={field.form_name} style={{ marginBottom: isBrandForm ? '40px' : '20px' }}>
                                {field.form_header && renderHeader(field)}
                                <LocationAutocomplete
                                    value={formData[field.store_key]}
                                    onSelect={address => handleLocationSelect(field.store_key, address, field.multiple)}
                                    placeholder={field.locationProps?.placeholder}
                                    searchOptions={{ types: ['(regions)'] }}
                                    brandDashboard={isBrandForm}
                                    onDelete={index => {
                                        if (field.multiple) {
                                            const initVal = Array.isArray(formData[field.store_key])
                                                ? formData[field.store_key]
                                                : [];
                                            const filtered = initVal.filter((_: any, i: number) => i !== index);
                                            handleChange(field.store_key, filtered);
                                        } else {
                                            handleChange(field.store_key, null);
                                        }
                                    }}
                                    locationOptions={field.additionalLocationOption}
                                    onSelectLocationOption={option => {
                                        handleChange(field.store_key, option?.value as any);
                                    }}
                                />
                            </div>
                        )
                    );
                case 'calendar':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomDatePicker
                                onChange={(newDate, timezone) => {
                                    if (timezone) {
                                        handleChange('timezone', timezone);
                                    }
                                    handleChange(field.store_key, newDate);
                                }}
                                key={field.form_name}
                                brandLuxe={isBrandForm}
                                maxDate={
                                    field.calendarProps &&
                                    field.calendarProps?.maxDate &&
                                    field.calendarProps?.maxDate === 0
                                        ? dayjs()
                                        : dayjs().add(500, 'years')
                                }
                                minDate={
                                    field.calendarProps?.minDateKey && formData?.[field.calendarProps?.minDateKey]
                                        ? formData?.[field.calendarProps?.minDateKey]
                                        : field.calendarProps && field.calendarProps?.minDate
                                        ? field.calendarProps?.minDate
                                        : dayjs()
                                }
                                value={(formData[field.store_key] as string) || ''}
                                withTime={field.calendarProps?.withTime}
                                sx={{
                                    mt: 1.5,
                                    ...(!isBrandForm
                                        ? { '& .MuiOutlinedInput-root': { bgcolor: EDITORIAL.surfaceContainer } }
                                        : {}),
                                }}
                                withTimezone={!!field.calendarProps?.withTime}
                                {...(field.calendarProps || {})}
                            />
                        </div>
                    );
                case 'radioSelection':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <RadioGroup
                                row
                                name={field.form_name}
                                value={formData[field.store_key] || ''}
                                onChange={e => handleChange(field.store_key, e.target.value)}>
                                {(field.form_options as FormOption[]).map(option => (
                                    <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: EDITORIAL.outline,
                                                    '&.Mui-checked': { color: EDITORIAL.primary },
                                                }}
                                            />
                                        }
                                        label={option.label}
                                        sx={{
                                            color: EDITORIAL.onSurfaceVariant,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontWeight: 600,
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    );
                case 'range': {
                    const { sx: rangeSliderSx, ...rangeSliderRest } = field.sliderProps || {};
                    const minBound = rangeSliderRest.min ?? 0;
                    const maxBound = rangeSliderRest.max ?? 100;

                    const normalizeRange = (raw: unknown): [number, number] => {
                        const arr = Array.isArray(raw) ? raw : [minBound, maxBound];
                        const a = Number(arr[0]);
                        const b = Number(arr[1]);
                        const x = Number.isFinite(a) ? a : minBound;
                        const y = Number.isFinite(b) ? b : maxBound;
                        const lo = Math.max(minBound, Math.min(maxBound, Math.min(x, y)));
                        const hi = Math.max(minBound, Math.min(maxBound, Math.max(x, y)));
                        return lo <= hi ? [lo, hi] : [hi, lo];
                    };

                    const pair = normalizeRange(formData[field.store_key]);

                    const configuredStep = rangeSliderRest.step;
                    const bumpStep =
                        configuredStep != null && Number(configuredStep) > 0 ? Number(configuredStep) : 1000;

                    const showBoundsInHeader =
                        field.store_key === 'employee_count' || field.store_key === 'revenue_range';
                    const boundsSuffix = showBoundsInHeader
                        ? ` (${minBound.toLocaleString('en-US')} – ${maxBound.toLocaleString('en-US')})`
                        : '';
                    const rangeHeaderNode = field.form_header ? (
                        <CustomHeaderWithStatus
                            field={field.store_key}
                            isRequired={field.required}
                            headerTitle={capitalizeFirst(field.form_header) + boundsSuffix}
                            themeStyle={themeStyle}
                            state={formData}
                            editorial
                            style={{ marginBottom: '12px' }}
                        />
                    ) : null;

                    if (isBrandForm) {
                        return (
                            <div key={field.form_name} style={{ marginBottom: '20px' }}>
                                <BrandRangePairFields
                                    pair={pair}
                                    minBound={minBound}
                                    maxBound={maxBound}
                                    bumpStep={bumpStep}
                                    fieldStyle={fieldStyle}
                                    header={rangeHeaderNode}
                                    inputVariant={field.store_key === 'revenue_range' ? 'number' : 'text'}
                                    onCommit={(lo, hi) => handleChange(field.store_key, [lo, hi])}
                                />
                            </div>
                        );
                    }

                    return (
                        <Box sx={editorialRangeSectionSx} key={field.form_name}>
                            {field.form_header && renderHeader(field)}
                            <Slider
                                value={(formData[field.store_key] as unknown as number[]) || [0, 100]}
                                onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                                valueLabelDisplay="auto"
                                min={0}
                                max={100}
                                sx={editorialSliderSx}
                                {...field.sliderProps}
                            />
                        </Box>
                    );
                }
                case 'slider': {
                    const { sx: singleSliderSx, ...singleSliderRest } = field.sliderProps || {};
                    const minBound = singleSliderRest.min ?? 0;
                    const maxBound = singleSliderRest.max ?? 100;
                    const step = singleSliderRest.step ?? 1;
                    const raw = formData[field.store_key] as unknown as number;
                    const num = Number(raw);
                    const singleVal = Number.isFinite(num) ? Math.max(minBound, Math.min(maxBound, num)) : minBound;

                    if (isBrandForm) {
                        return (
                            <div key={field.form_name} style={{ marginBottom: '20px' }}>
                                {field.form_header && renderHeader(field)}
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    type="number"
                                    placeholder={field.placeholder || 'Enter a number'}
                                    value={singleVal}
                                    onChange={e => {
                                        const v = e.target.value === '' ? minBound : Number(e.target.value);
                                        if (!Number.isFinite(v)) return;
                                        handleChange(field.store_key, Math.max(minBound, Math.min(maxBound, v)));
                                    }}
                                    inputProps={{ min: minBound, max: maxBound, step }}
                                    sx={{ ...fieldStyle, ...brandModalOutlinedInputSx }}
                                />
                            </div>
                        );
                    }

                    return (
                        <Box sx={editorialRangeSectionSx} key={field.form_name}>
                            {field.form_header && renderHeader(field)}
                            <Slider
                                value={(formData[field.store_key] as unknown as number) || 0}
                                onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                                valueLabelDisplay="auto"
                                sx={editorialSliderSx}
                                {...field.sliderProps}
                            />
                        </Box>
                    );
                }
                case 'currency':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CurrencyTextInput
                                value={(formData[field.store_key] as unknown as number) || 0}
                                supportedCurrency={field.currencyTextInputProps?.supportedCurrency}
                                brandChrome={isBrandForm}
                                onChange={({ currency, value, displayValue }) => {
                                    if (field.additional_store_keys) {
                                        handleChange(field.additional_store_keys[0], currency as any);
                                        handleChange(field.additional_store_keys[1], value as any);
                                        handleChange('displayCurrencyValue', displayValue as any);
                                    } else {
                                        handleChange(field.store_key, value);
                                    }
                                }}
                                sx={{
                                    color: EDITORIAL.onSurface,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: EDITORIAL.outlineVariant,
                                    },
                                }}
                            />
                        </div>
                    );
                case 'suggestionTextInput':
                    /** Allows us to only select one item when selecting */
                    const singleSelection =
                        field.suggestionTextInputProps &&
                        field.suggestionTextInputProps?.maximumSelectable &&
                        field.suggestionTextInputProps?.maximumSelectable < 2;
                    let list = field.suggestion!;

                    if (
                        field.suggestionTextInputProps &&
                        field.suggestionTextInputProps?.enableDynamicList &&
                        field.suggestionTextInputProps.dynamicList &&
                        field.suggestionTextInputProps.dependencyField &&
                        initialFormData
                    ) {
                        const dynamicList = field.suggestionTextInputProps.dynamicList || [];
                        const dependecyField = field.suggestionTextInputProps.dependencyField;
                        const depValue = initialFormData?.[dependecyField];

                        const value =
                            typeof depValue === 'string'
                                ? depValue
                                : depValue && typeof depValue === 'object' && 'value' in depValue
                                ? depValue.value
                                : null;

                        if (value && Array.isArray(dynamicList[value]) && !isEmpty(dynamicList[value])) {
                            list = dynamicList[value];
                        }
                    }
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px', position: 'relative' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomizedAutocomplete
                                onChange={text => {
                                    const newText = singleSelection ? text[0] : text;
                                    handleChange(field.store_key, newText);
                                }}
                                options={list}
                                maxSelectable={field?.suggestionTextInputProps?.maximumSelectable ?? 1}
                                brandSurface={isBrandForm}
                                initialValue={
                                    singleSelection && formData
                                        ? formData?.[field.store_key]
                                            ? [formData?.[field.store_key] as any]
                                            : []
                                        : (formData?.[field.store_key] as any)
                                }
                            />
                        </div>
                    );
                case 'link':
                    return <div>link</div>;
                case 'action_button':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <ActionRenderer
                                actionType={field.action_button_props?.form as string}
                                actionValues={formData[field.store_key] as any}
                                initialFormData={formData}
                                formField={field}
                                brandChrome={isBrandForm}
                                onSubmit={(data, parentKey, isMultiple, isEdit, editIndex) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    let body;
                                    if (isEdit && typeof editIndex === 'number') {
                                        // Replace the item at editIndex
                                        body = [...existingData];
                                        body[editIndex] = data;
                                    } else {
                                        // Add a new item or set the value
                                        body = isMultiple ? [...existingData, data] : data;
                                    }

                                    handleChange(field.store_key, body);
                                }}
                                onDelete={(card, index, actionType) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    if (index >= 0 && index < existingData.length) {
                                        const body = existingData.filter((_: any, i: number) => i !== index); // Remove the item at the given index
                                        handleChange(field.store_key, body);
                                    }
                                }}
                                onDuplicate={(card, actionType) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    const duplicatedItem = { ...card }; // Clone the item to avoid mutations
                                    const body = [...existingData, duplicatedItem]; // Append duplicated item

                                    handleChange(field.store_key, body);
                                }}
                                onAddClicked={() => {
                                    initialValueSet.current = false;
                                    onCreateNewClicked &&
                                        onCreateNewClicked(
                                            field.action_button_props?.form,
                                            field.store_key,
                                            formData,
                                            formData[field.store_key] ? formData[field.store_key].length : 0,
                                        );
                                }}
                                {...props}
                            />
                        </div>
                    );
                case 'search_add':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <SearchSelectComponent
                                initialValue={formData[field.store_key] as any}
                                onChange={value => {
                                    handleChange(field.store_key, value);
                                }}
                                multiple={field.multiple}
                                category={field.search_add_category}
                                innerWrapperSx={
                                    isBrandForm
                                        ? {
                                              border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              height: 52,
                                              borderRadius: '12px',
                                              overflow: 'hidden',
                                              px: 1.5,
                                              '&:hover': {
                                                  borderColor: 'rgba(255,255,255,0.32)',
                                                  boxShadow: 'none',
                                              },
                                              '&:focus-within': {
                                                  borderColor: BRAND_MODAL_ACCENT,
                                                  boxShadow: 'none',
                                              },
                                          }
                                        : {}
                                }
                                listPaperSx={
                                    isBrandForm
                                        ? {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                              boxShadow: '0 10px 26px rgba(0,0,0,0.45)',
                                          }
                                        : {}
                                }
                                optionRowSx={
                                    isBrandForm
                                        ? {
                                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              hoverBackgroundColor: 'rgba(255,255,255,0.06)',
                                              borderBottomColor: BRAND_OUTLINE_VARIANT,
                                          }
                                        : undefined
                                }
                                onAddClick={() => {
                                    handleOnAddClickForSearchSuggestion(
                                        field.search_add_category,
                                        field.search_add_category_form,
                                    );
                                }}
                                hidePopupIndicator={isBrandForm}
                            />
                        </div>
                    );
                case 'picture':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <PictureUploader
                                onChange={(images, rawFiles, alternateKey) => {
                                    handleChange(field.store_key, images);
                                    handleChange('rawFiles', rawFiles as File[]);
                                    if (alternateKey) {
                                        handleChange(alternateKey, rawFiles as File[]);
                                    }
                                }}
                                onRemove={() => {
                                    handleChange(field.store_key, null);
                                    handleChange('rawFiles', null);
                                }}
                                multiple={field.multiple}
                                initialImageSrc={formData[field.store_key]}
                                maxCount={field.max_count}
                                supportedTypes={field.supported_media_types}
                                brandChrome={isBrandForm}
                                {...field.pictureProps}
                            />
                        </div>
                    );
                case 'upload':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <UploadComponent
                                onChange={value => handleChange(field.store_key, value)}
                                initialFile={formData[field.store_key] as any}
                                uploadFormats={extensionToMimeType(field.supported_upload_formats)}
                                visualVariant={isBrandForm ? 'brand' : 'default'}
                            />
                        </div>
                    );
                case 'video':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <MediaAdderComponent
                                mediaValues={
                                    Array.isArray(formData[field.store_key])
                                        ? formData[field.store_key]
                                        : formData[field.store_key]
                                        ? [formData[field.store_key]]
                                        : []
                                }
                                mediaType={field.media_type ?? 'projects'}
                                onDelete={payload => {
                                    const cur = formData[field.store_key];
                                    const list = Array.isArray(cur) ? cur : cur ? [cur] : [];
                                    const next = list.filter((sel: any) => sel._id !== payload._id);
                                    handleChange(field.store_key, next.length ? next : null);
                                }}
                                onSubmit={payload => {
                                    handleChange(field.store_key, payload);
                                }}
                                maxSelection={field.max_count ?? 1}
                                visualVariant={isBrandForm ? 'brand' : 'default'}
                            />
                        </div>
                    );
                case 'autoComplete':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomizedMultipleInput
                                autoCompleteOptions={[]}
                                placeholder={field.autoCompleteProps?.placeholder}
                                maximumSelectable={field.autoCompleteProps?.maximumSelectable}
                                initialValue={formData[field.store_key] as any}
                                onChange={arg => {
                                    handleChange(field.store_key, arg);
                                }}
                                brandForm={isBrandForm}
                                style={
                                    isBrandForm
                                        ? { borderRadius: '12px' }
                                        : {
                                              borderRadius: '12px',
                                              backgroundColor: EDITORIAL.surfaceLow,
                                              border: `1px solid ${EDITORIAL.outlineVariant}`,
                                          }
                                }
                                loading={false} // API Call loading
                                {...field.autoCompleteProps}
                            />
                        </div>
                    );
                case 'mediaAdder':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <MediaAdderComponent
                                mediaValues={formData[field.store_key] as any}
                                mediaType={field.media_type}
                                onDelete={payload => {
                                    /** Delete teh selection */
                                    const selections = formData[field.store_key].filter(
                                        (sel: any) => sel._id !== payload._id,
                                    );
                                    handleChange(field.store_key, selections);
                                }}
                                onSubmit={payload => {
                                    handleChange(field.store_key, payload);
                                }}
                                maxSelection={field.max_count ?? 10}
                                visualVariant={isBrandForm ? 'brand' : 'default'}
                            />
                        </div>
                    );
                case 'checkbox':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            <FormControlLabel
                                title={field.form_label}
                                control={
                                    <Checkbox
                                        checked={(formData[field.store_key] as unknown as boolean) || false}
                                        onChange={() => {
                                            handleChange(field.store_key, !formData[field.store_key]);
                                        }}
                                        sx={{
                                            color: EDITORIAL.outline,
                                            '&.Mui-checked': {
                                                color: EDITORIAL.primary,
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: EDITORIAL.primary,
                                            },
                                        }}
                                    />
                                }
                                label={renderHeader(field, true)}
                            />
                            {field.tooltip &&
                                formData[field.store_key] &&
                                field.tooltip?.map((tip, i) => (
                                    <Typography
                                        key={i}
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: EDITORIAL.onSurfaceVariant,
                                            mt: 1,
                                        }}>
                                        {tip.message}
                                    </Typography>
                                ))}
                        </div>
                    );
                default:
                    return null;
            }
        };

        const isFormComplete = () => {
            const fields =
                formFields &&
                formFields.filter(field => {
                    // Check if field is rendered based on dependency or other logic
                    if (field.dependency) {
                        const dependentValue = formData[field.dependency.depends_on];
                        const condition = field.dependency.condition;
                        let shouldRender = false;

                        if (isObject(dependentValue) && 'value' in dependentValue) {
                            shouldRender =
                                condition?.equality === '!=='
                                    ? dependentValue?.value !== condition?.value
                                    : dependentValue?.value === condition?.value;
                        } else {
                            shouldRender =
                                condition?.equality === '!=='
                                    ? dependentValue !== condition?.value
                                    : dependentValue === condition?.value;
                        }

                        if (isString(dependentValue)) {
                            shouldRender = !isEmpty(dependentValue);
                        }

                        return shouldRender; // Include field only if it should render
                    }
                    return true; // Include all non-dependent fields
                });
            const isComplete = fields.every(field => {
                if (field.required) {
                    return !isEmptyValue(formData[field.store_key]);
                }
                return true;
            }); // Check if any field is filled
            return isComplete;
        };

        const hasChanged = () => !_.isEqual(initialValue, formData);

        const loadDevData = () => {
            if (devData) {
                setChecked(!checked);
                if (!checked) {
                    setFormData(devData);
                } else {
                    setFormData({});
                }
            }
        };

        return (
            <Box
                sx={{
                    height: '100%',
                    width: '100%',
                    maxHeight: { xs: '100dvh' },
                    overflowY: 'auto',
                    position: 'relative',
                    ...editorialScrollRootSx,
                    ...(isBrandForm ? (brandModalFieldGutterSx as Record<string, unknown>) : {}),
                    pb: {
                        xs: 'calc(24px + env(safe-area-inset-bottom))',
                        sm: 2,
                    },
                }}>
                {isDev && (
                    <FormControlLabel
                        title="toggle dev data"
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={loadDevData}
                                sx={{
                                    color: theme[themeStyle].gray_sidebar,
                                    '&.Mui-checked': {
                                        color: theme[themeStyle].highlight,
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: theme[themeStyle].highlight,
                                    },
                                }}
                            />
                        }
                        label={checked ? 'Checked' : 'Unchecked'} // Label changes based on state
                    />
                )}

                <form>
                    {/* Rendering form fields */}
                    {formFields && formFields.map(field => renderField(field))}

                    {handleSubmitInternally && (
                        <Box component="footer" sx={editorialStickyFooterSx}>
                            <CustomButton
                                label="Save"
                                onClick={handleSubmit}
                                customStyle={{ width: '100%', minHeight: 52 }}
                                textStyle={editorialSubmitTextStyle}
                                sx={editorialSubmitButtonSx}
                                disabled={!isFormComplete() || (isEdit && !hasChanged())}
                                loading={loading}
                            />
                        </Box>
                    )}
                </form>
            </Box>
        );
    },
);

export { FormGeneratorWithoutModal };
